import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Plus, Power, Trash2, Sparkles, Loader2, Megaphone, Mail, Send, X } from 'lucide-react';

export default function AdminMarketing() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [blastOpen, setBlastOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiOut, setAiOut] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInfo, setAiInfo] = useState('Spring rehab promotion - 20% off lower back therapy package, valid for 4 weeks.');
  const [form, setForm] = useState({
    name: '', code: 'SPRING20', discount_percent: 20,
    valid_from: new Date().toISOString().slice(0,10),
    valid_to: new Date(Date.now()+30*24*60*60*1000).toISOString().slice(0,10),
    description: '', audience: 'all',
  });

  const load = () => api.get('/promotions').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    await api.post('/promotions', { ...form, discount_percent: parseFloat(form.discount_percent) });
    setShow(false); load();
  };
  const toggle = async (id) => { await api.put(`/promotions/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/promotions/${id}`); load(); };

  const runAI = async () => {
    setAiLoading(true); setAiOut('');
    try {
      const { data } = await api.post('/ai/generate', {
        prompt: `Write a short marketing campaign description (2 sentences) and a catchy promo code idea for: ${aiInfo}`,
        provider: 'anthropic', purpose: 'marketing',
      });
      setAiOut(data.text);
    } catch (e) {
      setAiOut('AI error: ' + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CAMPAIGNS</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Marketing</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>setBlastOpen(true)} className="btn-secondary inline-flex items-center gap-2" data-testid="email-blast-btn">
            <Mail className="w-4 h-4" /> Email blast
          </button>
          <button onClick={()=>setAiOpen(!aiOpen)} className="btn-secondary inline-flex items-center gap-2" data-testid="ai-marketing-btn">
            <Sparkles className="w-4 h-4" /> AI copy
          </button>
          <button onClick={()=>setShow(!show)} className="btn-primary inline-flex items-center gap-2" data-testid="new-promo-btn">
            <Plus className="w-4 h-4" /> New promotion
          </button>
        </div>
      </div>

      {blastOpen && <EmailBlastModal onClose={()=>setBlastOpen(false)}/>}

      {aiOpen && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-3">AI marketing copywriter</h3>
          <textarea className="input min-h-[80px]" value={aiInfo} onChange={e=>setAiInfo(e.target.value)} data-testid="ai-marketing-prompt" />
          <div className="mt-3">
            <button onClick={runAI} disabled={aiLoading} className="btn-primary inline-flex items-center gap-2" data-testid="ai-marketing-run">
              {aiLoading && <Loader2 className="w-4 h-4 animate-spin" />} Generate copy
            </button>
          </div>
          {aiOut && <div className="mt-4 surface border border-soft p-4 text-sm whitespace-pre-wrap" data-testid="ai-marketing-output">{aiOut}</div>}
        </div>
      )}

      {show && (
        <div className="admin-card mb-6">
          <h3 className="font-heading font-semibold mb-4">New promotion</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Name" v={form.name} on={v=>setForm({...form, name:v})} testid="promo-name" />
            <Input label="Code" v={form.code} on={v=>setForm({...form, code:v.toUpperCase()})} testid="promo-code" />
            <Input label="Discount %" type="number" v={form.discount_percent} on={v=>setForm({...form, discount_percent:v})} />
            <Input label="Valid from" type="date" v={form.valid_from} on={v=>setForm({...form, valid_from:v})} />
            <Input label="Valid to" type="date" v={form.valid_to} on={v=>setForm({...form, valid_to:v})} />
            <div>
              <label className="text-xs text-fg-muted">Audience</label>
              <select className="input" value={form.audience} onChange={e=>setForm({...form, audience: e.target.value})}>
                <option value="all">All</option><option value="new">New patients</option><option value="existing">Existing</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs text-fg-muted">Description</label>
              <textarea className="input" value={form.description} onChange={e=>setForm({...form, description: e.target.value})} />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={submit} className="btn-primary" disabled={!form.name || !form.code} data-testid="promo-save">Save</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <div key={p.promotion_id} className="bento-card relative" data-testid={`promo-card-${p.promotion_id}`}>
            {p.lifecycle === 'live' && <div className="absolute top-3 right-3 badge-success">Live</div>}
            {p.lifecycle === 'scheduled' && <div className="absolute top-3 right-3 badge-neutral">Scheduled · auto-release {p.valid_from}</div>}
            {p.lifecycle === 'expired' && <div className="absolute top-3 right-3 badge-neutral">Expired</div>}
            {p.lifecycle === 'paused' && <div className="absolute top-3 right-3 badge-neutral">Paused</div>}
            <Megaphone className="w-5 h-5 text-accent mb-3" />
            <h3 className="font-heading font-bold text-xl">{p.name}</h3>
            <div className="font-mono tracking-[0.2em] text-2xl mt-2 text-brand">{p.code}</div>
            <div className="text-sm text-fg-secondary mt-2">{p.description}</div>
            <div className="text-xs text-fg-muted mt-3">
              {p.discount_percent}% off · {p.valid_from} → {p.valid_to} · {p.audience}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={()=>toggle(p.promotion_id)} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid={`toggle-${p.promotion_id}`}>
                <Power className="w-3.5 h-3.5"/> Toggle
              </button>
              <button onClick={()=>remove(p.promotion_id)} className="text-[#B83A3A] inline-flex items-center gap-1 text-xs" data-testid={`del-promo-${p.promotion_id}`}>
                <Trash2 className="w-3.5 h-3.5"/> Delete
              </button>
            </div>
          </div>
        ))}
        {!items.length && <div className="text-fg-muted text-sm">No promotions yet.</div>}
      </div>
    </div>
  );
}

function Input({ label, v, on, type='text', testid }) {
  return (
    <div>
      <label className="text-xs text-fg-muted">{label}</label>
      {type === 'date'
        ? <DateInput value={v} onChange={e=>on(e.target.value)} data-testid={testid} />
        : <input className="input" value={v} type={type} onChange={e=>on(e.target.value)} data-testid={testid} />}
    </div>
  );
}

function EmailBlastModal({ onClose }) {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState('Special offer from Physiogenic');
  const [body, setBody] = useState('<p>Hi {name},</p><p>We have a great offer for you this month — use code <strong>{promo_code}</strong> at checkout for 20% off your next session.</p>');
  const [promoCode, setPromoCode] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/patients').then(r => setPatients(r.data));
  }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(patients.map(p => p.user_id)));
  const clearAll = () => setSelected(new Set());

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/marketing/email-blast', {
        patient_ids: Array.from(selected),
        subject, body_html: body,
        promo_code: promoCode || undefined,
      });
      setResult(data);
    } catch (e) {
      alert('Send failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="blast-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-brand"/> Marketing email blast</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">
          {result ? (
            <div className="surface border border-soft p-5 rounded-lg" data-testid="blast-result">
              <h3 className="font-heading font-bold text-xl text-brand">Sent.</h3>
              <p className="text-sm mt-2">{result.sent} delivered · {result.skipped} skipped (opt-out) · {result.failed} failed.</p>
              <button onClick={onClose} className="btn-primary mt-4 text-sm">Done</button>
            </div>
          ) : (
            <>
              <Input label="Subject" v={subject} on={setSubject} testid="blast-subject"/>
              <div className="mt-3">
                <label className="text-xs text-fg-muted">Promo code (optional — replaces {'{promo_code}'} in body)</label>
                <input className="input font-mono" value={promoCode} onChange={e=>setPromoCode(e.target.value.toUpperCase())} placeholder="SPRING20" data-testid="blast-promo"/>
              </div>
              <div className="mt-3">
                <label className="text-xs text-fg-muted">Body HTML (use {'{name}'} for personalization)</label>
                <textarea className="input min-h-[150px] font-mono text-xs" value={body} onChange={e=>setBody(e.target.value)} data-testid="blast-body"/>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-heading font-semibold">Pick recipients ({selected.size}/{patients.length})</h4>
                  <div className="flex gap-2 text-xs">
                    <button onClick={selectAll} className="text-brand" data-testid="blast-select-all">All</button>
                    <button onClick={clearAll} className="text-fg-muted">Clear</button>
                  </div>
                </div>
                <div className="surface border border-soft rounded-lg max-h-[200px] overflow-y-auto">
                  {patients.map(p => (
                    <label key={p.user_id} className="flex items-center gap-2 p-2 border-b border-soft text-sm hover:bg-[var(--bg-tertiary)] cursor-pointer">
                      <input type="checkbox" checked={selected.has(p.user_id)} onChange={()=>toggle(p.user_id)} data-testid={`blast-pick-${p.user_id}`}/>
                      <span className="flex-1">{p.full_name}</span>
                      <span className="text-xs text-fg-muted">{p.email}</span>
                    </label>
                  ))}
                  {!patients.length && <div className="text-center text-fg-muted py-4 text-sm">No patients.</div>}
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
                <button onClick={send} disabled={sending || !selected.size || !subject || !body}
                        className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="blast-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
                  Send to {selected.size}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
